import { nowEpochMilliseconds } from "@nijiurachan/js/util/time";
import {
  create,
  type Mutate,
  type StoreApi,
  type UseBoundStore,
} from "zustand";
import {
  createJSONStorage,
  persist,
  type StateStorage,
} from "zustand/middleware";
import {
  type HistoryThreadRef,
  isHistoryThreadRef,
} from "@/features/history/historyIdentity";
import { resolveSafePersistStorage } from "@/shared/lib/safePersistStorage";
import { isUuidThreadId } from "@/shared/lib/threadIdMigration";

export const POSTED_HISTORY_MAX_ITEMS = 100;
export const POSTED_HISTORY_MAX_AGE_MS: number = 30 * 24 * 60 * 60 * 1_000;
const POSTED_HISTORY_STORAGE_KEY = "nijiurachan-posted-history-by-board-v1";

export interface PostedHistoryItem extends HistoryThreadRef {
  ts: number;
}

export function selectActivePostedItems(
  posted: readonly PostedHistoryItem[],
  currentTimestamp: number = nowEpochMilliseconds(),
): PostedHistoryItem[] {
  return posted
    .filter((item) => currentTimestamp - item.ts <= POSTED_HISTORY_MAX_AGE_MS)
    .slice();
}

interface PostedHistoryState {
  posted: PostedHistoryItem[];
  addPosted: (boardSlug: string, threadId: string) => void;
  removeFromPostedHistory: (boardSlug: string, threadId: string) => void;
  clearPostedHistory: (boardSlugs?: readonly string[]) => void;
  getPostedItems: () => PostedHistoryItem[];
}

export type PostedHistoryStore = UseBoundStore<
  Mutate<StoreApi<PostedHistoryState>, [["zustand/persist", unknown]]>
>;

export function createPostedHistoryStore(
  storage?: StateStorage,
): PostedHistoryStore {
  try {
    storage ??= globalThis.localStorage;
  } catch {
    // localStorage自体を取得できない場合も、メモリ上の操作を継続する。
  }
  const persistedStorage = resolveSafePersistStorage(
    storage ? withPostedMemoryFallback(storage) : undefined,
  );
  return create<PostedHistoryState>()(
    persist(
      (set, get) => ({
        posted: [],
        addPosted: (boardSlug: string, threadId: string) => {
          const now = nowEpochMilliseconds();
          set((state) => {
            const latestPosted = readPersistedPosted(
              persistedStorage,
              state.posted,
            );
            return {
              posted: [
                { boardSlug, threadId, ts: now },
                ...latestPosted.filter(
                  (item) =>
                    (item.boardSlug !== boardSlug ||
                      item.threadId !== threadId) &&
                    now - item.ts <= POSTED_HISTORY_MAX_AGE_MS,
                ),
              ].slice(0, POSTED_HISTORY_MAX_ITEMS),
            };
          });
        },
        removeFromPostedHistory: (boardSlug: string, threadId: string) =>
          set((state) => ({
            posted: readPersistedPosted(persistedStorage, state.posted).filter(
              (item) =>
                item.boardSlug !== boardSlug || item.threadId !== threadId,
            ),
          })),
        clearPostedHistory: (boardSlugs?: readonly string[]) =>
          set((state) => {
            if (!boardSlugs) return { posted: [] };
            return {
              posted: readPersistedPosted(
                persistedStorage,
                state.posted,
              ).filter((item) => !boardSlugs.includes(item.boardSlug)),
            };
          }),
        getPostedItems: () => selectActivePostedItems(get().posted),
      }),
      {
        name: POSTED_HISTORY_STORAGE_KEY,
        version: 1,
        storage: createJSONStorage(() => persistedStorage),
        merge: (persistedState, currentState) =>
          persistedState === undefined
            ? currentState
            : { ...currentState, ...migratePostedHistory(persistedState) },
      },
    ),
  );
}

export const usePostedHistoryStore: PostedHistoryStore =
  createPostedHistoryStore();

export function migratePostedHistory(persisted: unknown): {
  posted: PostedHistoryItem[];
} {
  if (!isRecord(persisted) || !Array.isArray(persisted.posted)) {
    return { posted: [] };
  }
  const now = nowEpochMilliseconds();
  return {
    posted: persisted.posted.filter(
      (item): item is PostedHistoryItem =>
        isRecord(item) &&
        isHistoryThreadRef(item) &&
        isUuidThreadId(item.threadId) &&
        typeof item.ts === "number" &&
        Number.isFinite(item.ts) &&
        now - item.ts <= POSTED_HISTORY_MAX_AGE_MS,
    ),
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readPersistedPosted(
  storage: StateStorage,
  fallback: PostedHistoryItem[],
): PostedHistoryItem[] {
  const serialized = storage.getItem(POSTED_HISTORY_STORAGE_KEY);
  if (typeof serialized !== "string") return fallback;
  try {
    const persisted = JSON.parse(serialized) as unknown;
    if (
      !isRecord(persisted) ||
      persisted.version !== 1 ||
      !isRecord(persisted.state)
    ) {
      return fallback;
    }
    return migratePostedHistory(persisted.state).posted;
  } catch {
    return fallback;
  }
}

function withPostedMemoryFallback(storage: StateStorage): StateStorage {
  type PendingWrite = { value: string | null };
  let shadow: PendingWrite | undefined;
  let previousWrite: Promise<void> | undefined;

  function write(value: string | null, operation: () => unknown): unknown {
    const pending = { value };
    shadow = pending;
    const confirm = (): void => {
      if (shadow === pending) shadow = undefined;
    };
    const execute = (): void | Promise<void> => {
      const result = operation();
      return result instanceof Promise ? result.then(confirm) : confirm();
    };
    const result = previousWrite ? previousWrite.then(execute) : execute();
    if (result instanceof Promise) {
      const settled = result.then(
        () => undefined,
        () => undefined,
      );
      previousWrite = settled;
      void settled.then(() => {
        if (previousWrite === settled) previousWrite = undefined;
      });
    }
    return result;
  }

  return {
    getItem(name: string): string | null | Promise<string | null> {
      if (shadow) return shadow.value;
      const stored = storage.getItem(name);
      return stored instanceof Promise
        ? stored.catch(() => shadow?.value ?? null)
        : stored;
    },
    setItem(name: string, value: string): unknown {
      return write(value, () => storage.setItem(name, value));
    },
    removeItem(name: string): unknown {
      return write(null, () => storage.removeItem(name));
    },
  };
}
